import React, { useEffect, useState } from "react";
import { getBrokerAccounts } from "../../../Services/BrokersApi";
import CreateVersionDialog from "./CreateVersionDialog";
import AddBrokerAccountDialog from "./AddBrokerAccountDialog";
import EditBrokerAccountDialog from "./EditBrokerAccountDialog";
import {
  Container,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  Stack,
  TextField,
  MenuItem,
  Chip,
  InputAdornment,
  IconButton,
  Card,
  Avatar,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  AddCircle as AddCircleIcon,
  FilterList as FilterListIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import { themeColors } from "../../../theme/colors";

const CustomerAccountsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);

  // pagination local state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // حالة حوار الإضافة
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // جديدة: حالة حوار التعديل والنسخة المختارة
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  // حالة فتح مكوّن إنشاء النسخة المُنفصل
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createUserId, setCreateUserId] = useState(null);

  // new: search state
  const [searchQuery, setSearchQuery] = useState("");

  // new: created_at date range filters (YYYY-MM-DD)
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");

  // new: system status filter ('all' | 'active' | 'inactive')
  const [systemStatusFilter, setSystemStatusFilter] = useState("all");

  // reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery, createdFrom, createdTo, systemStatusFilter]);

  // دالة جلب قابلة لإعادة الاستخدام (تُستدعى أيضاً بعد إنشاء حساب جديد)
  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("authToken");
      const data = await getBrokerAccounts(null, token);
      setAccounts(data || []);
      console.log("Broker accounts:", data);
    } catch (err) {
      setError((err && (err.message || err.msg || JSON.stringify(err))) || "حدث خطأ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // filtering by searchQuery across relevant account fields + created_at range
  const q = (searchQuery || "").trim().toLowerCase();
  const matches = (val) => val != null && String(val).toLowerCase().includes(q);
  const hasCreatedFilter = !!createdFrom || !!createdTo;
  const hasSystemFilter = systemStatusFilter && systemStatusFilter !== "all";

  const filteredAccounts = accounts.filter((acc) => {
    // matches text query (if provided)
    let matchesQuery = true;
    if (q && q.length > 0) {
      matchesQuery =
        matches(acc.name) ||
        matches(acc.phone) ||
        matches(acc.email) ||
        matches(acc.tenant_id) ||
        matches(acc.note) ||
        matches(String(acc.active)) ||
        matches(String(acc.status)) ||
        matches(String(acc.system_status)) ||
        matches(acc.created_at);
    }

    // matches created_at range (if provided)
    let matchesCreated = true;
    if (hasCreatedFilter) {
      const fStart = createdFrom ? Date.parse(createdFrom) : -Infinity;
      const fEnd = createdTo ? Date.parse(createdTo) + 24 * 60 * 60 * 1000 - 1 : Infinity; // include end day
      const tRaw = acc.created_at ?? null;
      const tVal = tRaw ? Date.parse(tRaw) : NaN;
      if (isNaN(tVal)) matchesCreated = false;
      else matchesCreated = tVal >= fStart && tVal <= fEnd;
    }

    // 3) matchesSystem: تحقق من حالة النظام إذا تم تطبيق الفلتر
    let matchesSystem = true;
    if (hasSystemFilter) {
      const s = acc && acc.system_status != null ? Number(acc.system_status) : undefined;
      if (systemStatusFilter === "active") matchesSystem = s === 1;
      else if (systemStatusFilter === "inactive") matchesSystem = s === 0;
    }

    return matchesQuery && matchesCreated && matchesSystem;
  });

  // slice data for current page (use filtered list)
  const pagedAccounts = filteredAccounts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const formatDate = (d) => {
    if (!d) return "-";
    try {
      return new Date(d).toLocaleString();
    } catch {
      return String(d);
    }
  };

  // فتح حوار التعديل مع تمرير بيانات الحساب
  const openEditDialog = (acc) => {
    setSelectedAccount(acc || null);
    setEditDialogOpen(true);
  };
  const closeEditDialog = () => {
    setSelectedAccount(null);
    setEditDialogOpen(false);
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header Section */}
        <Card
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${themeColors.primary.main} 0%, ${themeColors.primary.dark} 100%)`,
            color: "white",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: "rgba(255,255,255,0.25)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <PeopleIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                  حسابات العملاء
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  إدارة ومتابعة حسابات العملاء والنسخ
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
              sx={{
                bgcolor: "white",
                color: themeColors.primary.main,
                fontWeight: 600,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.95)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                },
                transition: "all 0.3s ease",
              }}
            >
              إضافة حساب جديد
            </Button>
          </Box>
        </Card>

        {/* Filters Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            border: `1px solid ${themeColors.border.light}`,
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <FilterListIcon sx={{ color: themeColors.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: themeColors.text.primary }}>
              البحث والتصفية
            </Typography>
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              variant="outlined"
              label="بحث شامل"
              placeholder="ابحث بالاسم، الهاتف، البريد..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flex: 1, minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: themeColors.text.secondary }} />
                  </InputAdornment>
                ),
                endAdornment:
                  searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              variant="outlined"
              label="من تاريخ"
              type="date"
              value={createdFrom}
              onChange={(e) => setCreatedFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              size="small"
              variant="outlined"
              label="إلى تاريخ"
              type="date"
              value={createdTo}
              onChange={(e) => setCreatedTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              select
              size="small"
              variant="outlined"
              label="حالة النظام"
              value={systemStatusFilter}
              onChange={(e) => setSystemStatusFilter(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">الكل</MenuItem>
              <MenuItem value="active">مفعل</MenuItem>
              <MenuItem value="inactive">غير مفعل</MenuItem>
            </TextField>
          </Box>
          {(searchQuery || createdFrom || createdTo || systemStatusFilter !== "all") && (
            <Box mt={2} display="flex" gap={1} alignItems="center">
              <Typography variant="caption" sx={{ color: themeColors.text.secondary }}>
                النتائج: {filteredAccounts.length} من {accounts.length}
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setSearchQuery("");
                  setCreatedFrom("");
                  setCreatedTo("");
                  setSystemStatusFilter("all");
                }}
                sx={{ color: themeColors.primary.main }}
              >
                إعادة تعيين الفلاتر
              </Button>
            </Box>
          )}
        </Paper>

        {loading && (
          <Box display="flex" justifyContent="center" my={8}>
            <CircularProgress size={60} sx={{ color: themeColors.primary.main }} />
          </Box>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(220, 53, 69, 0.15)",
            }}
          >
            {error}
          </Alert>
        )}

        {!loading && !error && accounts.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: "center",
              borderRadius: 3,
              border: `2px dashed ${themeColors.border.main}`,
            }}
          >
            <PeopleIcon sx={{ fontSize: 80, color: themeColors.text.light, mb: 2 }} />
            <Typography variant="h6" sx={{ color: themeColors.text.secondary, mb: 1 }}>
              لا توجد حسابات حالياً
            </Typography>
            <Typography variant="body2" sx={{ color: themeColors.text.secondary, mb: 3 }}>
              ابدأ بإضافة أول حساب عميل
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
              sx={{ bgcolor: themeColors.primary.main }}
            >
              إضافة حساب جديد
            </Button>
          </Paper>
        )}

        {!loading && !error && accounts.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${themeColors.border.light}`,
            }}
          >
            <TableContainer>
              <Table stickyHeader sx={{ direction: "rtl" }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      align="right"
                      sx={{
                        bgcolor: themeColors.background.default,
                        fontWeight: 700,
                        color: themeColors.text.primary,
                        borderBottom: `2px solid ${themeColors.primary.main}`,
                      }}
                    >
                      الاسم
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        bgcolor: themeColors.background.default,
                        fontWeight: 700,
                        color: themeColors.text.primary,
                        borderBottom: `2px solid ${themeColors.primary.main}`,
                      }}
                    >
                      الهاتف
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        bgcolor: themeColors.background.default,
                        fontWeight: 700,
                        color: themeColors.text.primary,
                        borderBottom: `2px solid ${themeColors.primary.main}`,
                      }}
                    >
                      البريد الإلكتروني
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        bgcolor: themeColors.background.default,
                        fontWeight: 700,
                        color: themeColors.text.primary,
                        borderBottom: `2px solid ${themeColors.primary.main}`,
                      }}
                    >
                      اسم النسخة
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        bgcolor: themeColors.background.default,
                        fontWeight: 700,
                        color: themeColors.text.primary,
                        borderBottom: `2px solid ${themeColors.primary.main}`,
                      }}
                    >
                      الحالة
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        bgcolor: themeColors.background.default,
                        fontWeight: 700,
                        color: themeColors.text.primary,
                        borderBottom: `2px solid ${themeColors.primary.main}`,
                      }}
                    >
                      ملاحظات
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        bgcolor: themeColors.background.default,
                        fontWeight: 700,
                        color: themeColors.text.primary,
                        borderBottom: `2px solid ${themeColors.primary.main}`,
                      }}
                    >
                      تاريخ الإنشاء
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        bgcolor: themeColors.background.default,
                        fontWeight: 700,
                        color: themeColors.text.primary,
                        borderBottom: `2px solid ${themeColors.primary.main}`,
                      }}
                    >
                      الإجراءات
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedAccounts.map((acc, idx) => (
                    <TableRow
                      key={acc.id}
                      sx={{
                        "&:hover": {
                          bgcolor: themeColors.background.hover,
                        },
                        "&:nth-of-type(even)": {
                          bgcolor: "rgba(0,0,0,0.02)",
                        },
                      }}
                    >
                      <TableCell align="right" sx={{ fontWeight: 600, color: themeColors.text.primary }}>
                        {acc.name || "-"}
                      </TableCell>
                      <TableCell align="right" sx={{ color: themeColors.text.secondary }}>
                        {acc.phone || "-"}
                      </TableCell>
                      <TableCell align="right" sx={{ color: themeColors.text.secondary }}>
                        {acc.email || "-"}
                      </TableCell>
                      <TableCell align="right">
                        {acc.tenant_id ? (
                          <Chip
                            label={acc.tenant_id}
                            size="small"
                            sx={{
                              bgcolor: `${themeColors.primary.main}15`,
                              color: themeColors.primary.main,
                              fontWeight: 600,
                            }}
                          />
                        ) : (
                          <Chip label="لا يوجد" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title={acc.active === 1 ? "الحساب يعمل" : "الحساب لا يعمل"}>
                            <Chip
                              label={acc.active === 1 ? "يعمل" : "لا يعمل"}
                              size="small"
                              sx={{
                                bgcolor: acc.active === 1 ? `${themeColors.success}15` : `${themeColors.error}15`,
                                color: acc.active === 1 ? themeColors.success : themeColors.error,
                                fontWeight: 600,
                              }}
                            />
                          </Tooltip>
                          <Tooltip title={acc.system_status === 1 ? "النظام مفعل" : "النظام غير مفعل"}>
                            <Chip
                              label={acc.system_status === 1 ? "مفعل" : "معطل"}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: acc.system_status === 1 ? themeColors.success : themeColors.error,
                                color: acc.system_status === 1 ? themeColors.success : themeColors.error,
                              }}
                            />
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ color: themeColors.text.secondary, fontSize: "0.875rem" }}>
                        {acc.note || "-"}
                      </TableCell>
                      <TableCell align="right" sx={{ color: themeColors.text.secondary, fontSize: "0.875rem" }}>
                        {formatDate(acc.created_at)}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="تعديل البيانات">
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(acc)}
                              sx={{
                                color: themeColors.primary.main,
                                "&:hover": {
                                  bgcolor: `${themeColors.primary.main}15`,
                                },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {acc?.tenant_id == null && (
                            <Tooltip title="إنشاء نسخة جديدة">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setCreateUserId(acc?.id ?? acc?.user_id ?? null);
                                  setCreateDialogOpen(true);
                                }}
                                sx={{
                                  color: themeColors.success,
                                  "&:hover": {
                                    bgcolor: `${themeColors.success}15`,
                                  },
                                }}
                              >
                                <AddCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredAccounts.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="عدد الصفوف:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
              sx={{
                borderTop: `1px solid ${themeColors.border.light}`,
                bgcolor: themeColors.background.default,
              }}
            />
          </Paper>
        )}

        <AddBrokerAccountDialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          onCreated={() => {
            // بعد إنشاء الحساب: إعادة جلب البيانات وإغلاق الحوار
            fetchAccounts();
            setAddDialogOpen(false);
          }}
        />

        {/* حوار التعديل */}
        <EditBrokerAccountDialog
          open={editDialogOpen}
          account={selectedAccount}
          onClose={closeEditDialog}
          onUpdated={() => {
            fetchAccounts();
            closeEditDialog();
          }}
        />

        {/* مكوّن الحوار المنفصل لإنشاء النسخة */}
        <CreateVersionDialog
          open={createDialogOpen}
          userId={createUserId}
          onClose={() => {
            setCreateDialogOpen(false);
            setCreateUserId(null);
          }}
          onCreated={() => {
            fetchAccounts();
          }}
        />
      </Container>
    </Box>
  );
};

export default CustomerAccountsPage;
